import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from "react"

const RESIZE_DRAG_THRESHOLD_PX = 4

export type UseResizablePanelWidthOptions = {
  storageKey: string
  defaultWidth: number
  minWidth: number
  maxWidth: number
  /** Upper bound as a share of the container width (e.g. 0.4 → panel never exceeds 40%). */
  maxWidthRatio?: number
  /** Minimum width reserved for the sibling (content) column while dragging. */
  minSiblingWidth?: number
  /** Container whose width is used to clamp against maxWidthRatio / minSiblingWidth. */
  containerRef?: RefObject<HTMLElement | null>
  /**
   * Which edge of the panel is dragged.
   * "trailing" (default) — panel on the right; pointer moves left → wider.
   * "leading" — panel on the left; pointer moves right → wider.
   */
  edge?: "leading" | "trailing"
}

function readStoredWidth(storageKey: string, defaultWidth: number, minWidth: number, maxWidth: number): number {
  try {
    const raw = localStorage.getItem(storageKey)
    if (raw == null) return defaultWidth
    const n = Number(raw)
    if (!Number.isFinite(n)) return defaultWidth
    return Math.min(maxWidth, Math.max(minWidth, n))
  } catch {
    return defaultWidth
  }
}

function effectiveMaxWidth(
  minWidth: number,
  maxWidth: number,
  containerWidth: number | undefined,
  maxWidthRatio: number | undefined,
  minSiblingWidth: number | undefined,
): number {
  let max = maxWidth
  if (containerWidth != null && containerWidth > 0) {
    if (maxWidthRatio != null) max = Math.min(max, containerWidth * maxWidthRatio)
    if (minSiblingWidth != null) max = Math.min(max, containerWidth - minSiblingWidth)
  }
  return Math.max(minWidth, max)
}

/**
 * Pixel width for a resizable side panel with localStorage persistence.
 * A narrower container only caps the shown width; the chosen width comes back when it widens again.
 */
export function useResizablePanelWidth({
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth,
  maxWidthRatio,
  minSiblingWidth,
  containerRef,
  edge = "trailing",
}: UseResizablePanelWidthOptions) {
  const [preferredWidth, setPreferredWidth] = useState(() =>
    readStoredWidth(storageKey, defaultWidth, minWidth, maxWidth),
  )
  const [currentMaxWidth, setCurrentMaxWidth] = useState(maxWidth)
  const widthRef = useRef(preferredWidth)
  widthRef.current = preferredWidth
  const detachRef = useRef<(() => void) | null>(null)

  const resizeRef = useRef<{
    active: boolean
    dragging: boolean
    startX: number
    startWidth: number
  }>({ active: false, dragging: false, startX: 0, startWidth: 0 })

  const resolveMax = useCallback(() => {
    const containerWidth = containerRef?.current?.getBoundingClientRect().width
    return effectiveMaxWidth(minWidth, maxWidth, containerWidth, maxWidthRatio, minSiblingWidth)
  }, [containerRef, maxWidth, maxWidthRatio, minSiblingWidth, minWidth])

  // Keep the panel inside its share of the container when the viewport / shell drawer changes.
  useEffect(() => {
    const el = containerRef?.current
    const sync = () => setCurrentMaxWidth(resolveMax())
    sync()
    if (!el || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(sync)
    observer.observe(el)
    return () => observer.disconnect()
  }, [containerRef, resolveMax])

  const endResize = useCallback(
    (swallowClick: boolean) => {
      const state = resizeRef.current
      if (!state.active) return
      const wasDragging = state.dragging
      state.active = false
      state.dragging = false
      document.body.style.removeProperty("user-select")
      document.body.style.removeProperty("cursor")
      if (!wasDragging) return
      if (swallowClick) {
        const stopClick = (ev: MouseEvent) => {
          ev.preventDefault()
          ev.stopPropagation()
        }
        window.addEventListener("click", stopClick, { capture: true, once: true })
      }
      try {
        localStorage.setItem(storageKey, String(widthRef.current))
      } catch {
        // ignore quota / private mode
      }
    },
    [storageKey],
  )

  const onResizePointerMove = useCallback(
    (e: PointerEvent) => {
      const state = resizeRef.current
      if (!state.active) return
      const dx = e.clientX - state.startX
      if (!state.dragging) {
        if (Math.abs(dx) < RESIZE_DRAG_THRESHOLD_PX) return
        state.dragging = true
        document.body.style.userSelect = "none"
        document.body.style.cursor = "ew-resize"
      }
      // Trailing panel: move left → wider. Leading panel: move right → wider.
      const max = resolveMax()
      const delta = edge === "leading" ? dx : -dx
      const next = Math.min(max, Math.max(minWidth, state.startWidth + delta))
      widthRef.current = next
      setCurrentMaxWidth(max)
      setPreferredWidth(next)
    },
    [edge, minWidth, resolveMax],
  )

  useEffect(() => {
    return () => {
      detachRef.current?.()
      document.body.style.removeProperty("user-select")
      document.body.style.removeProperty("cursor")
    }
  }, [])

  const onResizePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0) return
      e.preventDefault()
      detachRef.current?.()
      resizeRef.current = {
        active: true,
        dragging: false,
        startX: e.clientX,
        startWidth: Math.min(widthRef.current, resolveMax()),
      }
      function detach() {
        window.removeEventListener("pointermove", onResizePointerMove)
        window.removeEventListener("pointerup", onUp)
        window.removeEventListener("pointercancel", onCancel)
        detachRef.current = null
      }
      function onUp() {
        detach()
        endResize(true)
      }
      // Touch panning or a system gesture ends with pointercancel and no pointerup.
      function onCancel() {
        detach()
        endResize(false)
      }
      window.addEventListener("pointermove", onResizePointerMove)
      window.addEventListener("pointerup", onUp)
      window.addEventListener("pointercancel", onCancel)
      detachRef.current = detach
    },
    [endResize, onResizePointerMove, resolveMax],
  )

  const resetWidth = useCallback(() => {
    widthRef.current = defaultWidth
    setPreferredWidth(defaultWidth)
    try {
      localStorage.removeItem(storageKey)
    } catch {
      // ignore private mode
    }
  }, [defaultWidth, storageKey])

  return {
    width: Math.min(preferredWidth, currentMaxWidth),
    minWidth,
    maxWidth: currentMaxWidth,
    onResizePointerDown,
    resetWidth,
  }
}
